import React from 'react';

import Thumb from '../img/blogs/react.jpg';

const ReactJSXPropsError = {
    id: 11, permalink: "react-jsx-props-error", thumb: Thumb, postBy: "Wasek Bellah", postDate: "May 05, 2020", categories: "ReactJS", title: "React- JSX Props Error", shortDesc: "JSX is an XML/HTML-like syntax used by React that extends ECMAScript so that XML/HTML-like text can co-exist with JavaScript/React code. JSX is allowed to put HTML code in JavaScript code.", fullDesc: <>
        <h2>What is JSX?</h2>
        <p><strong>JSX</strong> is an <strong>XML/HTML</strong>-like syntax used by <strong>React</strong> that extends <strong>ECMAScript</strong> so that <strong>XML/HTML</strong>-like text can co-exist with <strong>JavaScript/React</strong> code. <strong>JSX</strong> is allowed to put <strong>HTML</strong> code in <strong>JavaScript</strong> code. and also put <strong>JavaScript</strong> code in <strong>HTML</strong> code. <strong>JSX</strong> provides syntactic sugar for the <code>React.createElement(component, props, ...children)</code> <strong>function</strong>.</p>

        <p><em>The <strong>JSX</strong> code</em>:</p>
        <pre className="code">
            &lt;MySelf name="Wasek Bellah" age=&#123;21&#125;&gt; <br />
                &nbsp; Hello World! <br />
            &lt;&#47;MySelf&gt;
        </pre>
        <p><em>Above code compiles(using <a href="https://babeljs.io/repl" target="_blank" rel="noreferrer noopener">Babel</a> compiler) into</em>:</p>
        <pre className="code">
            React.createElement(MySelf, &#123; <br />
                &nbsp; name: "Wasek Bellah", <br />
                &nbsp; age: 21 <br />
            &#125;, "Hello World!");
        </pre>


        <h2>User-Defined Components Must Be Capitalized</h2>
        <p>When an element type starts with a <strong>lowercase letter</strong>, it refers to a built-in element like <code>&lt;p&gt;</code> or <code>&lt;h1&gt;</code> and the result is passed to <code>React.createElement</code> in a string <code>'p'</code> or <code>'h1'</code>. Types that start with a <strong>capital letter</strong> like <code>&lt;Car &#47;&gt;</code>, it compiles to <code>React.createElement(Car)</code> and matches to a component defined or imported in your <strong>JavaScript</strong> file.</p>

        <p>Suggestions for component naming: start with a <strong>capital letter</strong>. If you have a component that starts with a <strong>lowercase letter</strong>, assign it to a <strong>capitalized variable</strong> before using it in <strong>JSX</strong>.</p>

        <pre className="code">
            const hello = props => &#123; <br />
                &nbsp; return &lt;p&gt;Hello &#123;props.name&#125;&lt;&#47;p&gt;; <br />
            &#125; <br />
            const HelloName = () => &#123; <br />
                &nbsp; return &lt;hello name="Wasek Bellah" &#47;&gt;; <br />
            &#125;
        </pre>
        <p>Above code will not work as expected</p>

        <p>To fix this, rename <code>hello</code> to <code>Hello</code> and use <code>&lt;Hello &#47;&gt;</code> when referring to it.</p>
        <pre className="code">
            const Hello = props => &#123; <br />
                &nbsp; return &lt;p&gt;Hello &#123;props.name&#125;&lt;&#47;p&gt;; <br />
            &#125; <br />
            const HelloName = () => &#123; <br />
                &nbsp; return &lt;Hello name="Wasek Bellah" &#47;&gt;; <br />
            &#125;
        </pre>


        {/* <h2>Props in JSX</h2>
        <p>There are several ways to specify props on <strong>JSX</strong>.</p>

        <h3>JavaScript Expressions as Props</h3>
        <p></p> */}


        <h2>Default Props</h2>
        <p><code>defaultProps</code> can be defined as a property on the <strong>component</strong> itself, to set the default <code>props</code> for the <strong>component</strong>. </p>

        <pre className="code">
            const MySelf = props => &#123; <br />
                &nbsp; return &lt;p&gt;My name is &#123;props.name&#125; and I am &#123;props.age&#125; years old.&lt;&#47;p&gt; <br />
            &#125; <br />
            MySelf.defaultProps = &#123; <br />
                &nbsp; name: 'Wasek Bellah', age: 21 <br />
            &#125;;<br /><br />

            return &lt;MySelf &#47;&gt;;
        </pre>
        <p>Here in <code>&lt;MySelf &#47;&gt;</code> component, by default <code>props.name</code> will be set to <code>'Wasek Bellah'</code> and <code>props.age</code> will be set to <code>'21'</code>.</p>


        <h2>Error Boundaries</h2>
        <p>A <strong>JavaScript</strong> error in a part of the <strong>UI</strong> should not break the whole application. To solve this issue <strong>React</strong> has an <strong>'error boundary'</strong>. It is a new concept of <strong>React 16</strong>.</p>

        <p><strong>Error boundaries</strong> are <strong>React components</strong> that catch <strong>JavaScript errors</strong> anywhere in their <strong>child component tree</strong>, log those errors, and display a fallback <strong>UI</strong> instead of the <strong>component tree</strong> that crashed.</p>

        <pre className="code">
            class ErrorBoundary extends React.Component &#123; <br />
                &nbsp; constructor(props) &#123; <br />
                    &nbsp;   super(props); <br />
                    &nbsp;   this.state = &#123; error: null, errorInfo: null &#125;; <br />
                &nbsp; &#125; <br />
                &nbsp; componentDidCatch(error, errorInfo) &#123; <br />
                    &nbsp;   this.setState(&#123; error: error, errorInfo: errorInfo &#125;); <br />
                &nbsp; &#125; <br />
                &nbsp; render() &#123; <br />
                    &nbsp;   if (this.state.errorInfo) &#123; <br />
                        &nbsp;     return ( <br />
                            &nbsp;       &lt;div&gt; <br />
                                &nbsp;         &lt;h2&gt;Something went wrong.&lt;&#47;h2&gt; <br />
                                &nbsp;         &lt;details style=&#123;&#123; whiteSpace: "pre-wrap" &#125;&#125;&gt; <br />
                                    &nbsp;           &#123;this.state.error &amp;&amp; this.state.error.toString()&#125; <br />
                                    &nbsp;           &lt;br &#47;&gt; <br />
                                    &nbsp;           &#123;this.state.errorInfo.componentStack&#125; <br />
                                &nbsp;         &lt;&#47;details&gt; <br />
                            &nbsp;       &lt;&#47;div&gt; <br />
                        &nbsp;     ); <br />
                    &nbsp;   &#125; <br />
                    &nbsp;   return this.props.children; <br />
                &nbsp; &#125; <br />
            &#125; <br /><br />

            class BuggyCounter extends React.Component &#123; <br />
                &nbsp; constructor(props) &#123; <br />
                    &nbsp;   super(props); <br />
                    &nbsp;   this.state = &#123; counter: 0 &#125;; <br />
                    &nbsp;   this.handleClick = this.handleClick.bind(this); <br />
                &nbsp; &#125; <br />
                &nbsp; handleClick() &#123; <br />
                    &nbsp;   this.setState((&#123; counter &#125;) =&gt; (&#123; counter: counter + 1 &#125;)); <br />
                &nbsp; &#125; <br />
                &nbsp; render() &#123; <br />
                    &nbsp;   if (this.state.counter &gt; 5) throw new Error("I crashed!"); <br />
                    &nbsp;   return &lt;h1 onClick=&#123;this.handleClick&#125;&gt;&#123;this.state.counter&#125;&lt;&#47;h1&gt;; <br />
                &nbsp; &#125; <br />
            &#125;
        </pre>

        <p><em>Then use as a regular component</em>:</p>
        <pre className="code">
            &lt;ErrorBoundary&gt; <br />
            &nbsp; &lt;BuggyCounter /&gt; <br />
            &lt;/ErrorBoundary&gt;
        </pre>

        <p>The <code>componentDidCatch()</code> method works like a JavaScript <code>catch&#123;&#125;</code> block, but for components. Only <strong>class components</strong> can be <strong>error boundaries</strong>.</p>

        <p><strong>Error boundaries</strong> only catch <strong>errors</strong> in the components below them in the tree. If any children throw an <strong>error in the boundaries</strong>, this error did not affect outside of <strong>boundaries</strong>.</p>


        <h2>Why Not Use try/catch?</h2>
        <p><code>try/catch</code> is great but it only works for imperative <strong>code</strong>.</p>

        <pre className="code">
            try &#123; <br />
                &nbsp; showElement(); <br />
            &#125; catch (error) &#123; <br />
                &nbsp; &#47;&#47; ... <br />
            &#125;
        </pre>

        <p><strong>Error boundaries</strong> preserve the declarative nature of the <strong>React</strong> and behave according to your expectations. For example, even if an error occurs in a <code>componentDidUpdate</code> method caused by a <code>setState</code> somewhere deep in the tree, it will still accurately propagate to the closest <strong>error boundary</strong>.</p>
    </>
};
export default ReactJSXPropsError;